/**
 * Menu Model
 */

define(function(require, exports, module) {
    'use strict';

    var _ = require('underscore');
    var urlUtil = require('util/url.util');
    var utils = require('utils');
    var BaseModel = require('common/base.model');
    var user = require('user');
    var device = require('util/device.util');
    var quickDepositService = require('modules/cashier/quick-deposit/quick-deposit.service.instance');
    var config = require('config');

    var MenuModel = BaseModel.extend({

        getInitialClonedItems: function() {
            var updateFriendlyUrl = function(item) {
                if (item.url === '/') {
                    item.friendlyURL = '/';
                }
            };

            var markMenuItemAsOpener = function(item) {
                var chatConfig = config.get('chatWidget');
                var inboxConfig = config.get('inboxWidget');

                if (inboxConfig && inboxConfig.enabled && inboxConfig.counterPage === item.url) {
                    item.inboxOpener = true;
                }

                if (chatConfig && chatConfig.enabled && chatConfig.counterPage === item.url) {
                    item.chatOpener = true;
                }
            };

            return utils.deepClone(config.get('menuItems'))
                ?.map(function(item) {
                    updateFriendlyUrl(item);

                    markMenuItemAsOpener(item);

                    if (item.children) {
                        item.children.forEach(function(child) {
                            // For case when item has the same link as one of children
                            updateFriendlyUrl(child);

                            // Find and mark inbox opener among children
                            markMenuItemAsOpener(child);
                        });

                        if (device.formFactor === 'mobile') {
                            item.expandable = _.isUndefined(item.expandablePageMobile) ? true : item.expandablePageMobile;
                            item.expandedByDefault =
                                _.isUndefined(item.expandedByDefaultMobile) ? false : item.expandedByDefaultMobile;
                        } else if (device.formFactor === 'tablet') {
                            item.expandable = _.isUndefined(item.expandablePageTablet) ? true : item.expandablePageTablet;
                            item.expandedByDefault =
                                _.isUndefined(item.expandedByDefaultTablet) ? false : item.expandedByDefaultTablet;
                        }
                    }

                    return item;
                }) ?? [];
        },

        /**
         * Get menu items filtered by role [user | guest]
         * @return {Object} All menu items available for current role
         */
        getFilteredByRoleItems: function() {
            var isQuickDepositAvailable = quickDepositService.isQuickDepositAvailable();
            var isAdmin = user.isAdmin();
            var qdPage = config.get('quickDepositPage');

            var checkRole = function(item) {
                var isGuestRole = item.roles && item.roles.includes('GUEST');
                var isUserRole = item.roles && item.roles.includes('USER');

                if (isAdmin
                    || isGuestRole && !isUserRole && !user.isLoggedIn()
                    || isUserRole && !isGuestRole && user.isLoggedIn()
                    || isUserRole && isGuestRole) {
                    return true;
                }
            };

            var needToHideMenuItemByQuickDepositAvailability = function(item) {
                if (isAdmin) {
                    return false;
                }

                return item.url === qdPage && !isQuickDepositAvailable;
            };

            return _.filter(this.getInitialClonedItems(), function(item) {
                item.isHidden = needToHideMenuItemByQuickDepositAvailability(item);

                if (item.children && item.children.length && checkRole(item)) {
                    item.children = _.filter(item.children, function(child) {
                        child.isHidden = needToHideMenuItemByQuickDepositAvailability(child);

                        return checkRole(child);
                    });

                    if (!item.children.length) {
                        item.children = null;
                        item.expandable = false;
                        item.expandedByDefault = false;
                    }
                }

                return checkRole(item);
            });
        },

        /**
         * Get URL of current page
         * @param  {string} friendlyURL
         * @return {string} Current page URL
         */
        getCurrentPageURL: function(friendlyURL) {
            var pageUrl = friendlyURL !== undefined ? friendlyURL : urlUtil.pathname;

            if (!pageUrl || urlUtil.getCleanUrl(pageUrl) === '/') {
                return urlUtil.getCleanUrl(config.get('rootPage'));
            }

            return urlUtil.getCleanUrl(this._handleLeaderBoardPage(pageUrl));
        },

        /**
         * Get siblings menu items for current URL
         * @param  {string} friendlyURL
         * @return {Array} List of items
         */
        getCurrentSiblings: function(friendlyURL) {
            function find(items, source) {
                var result;
                var item;

                for (var i = 0, len = items.length; i < len; i++) {
                    item = items[i];

                    if (item.friendlyURL === source) {
                        return items;
                    } else if (item.children) {
                        result = find(item.children, source);
                    }

                    if (result) {
                        return result;
                    }
                }
            }

            return this._findAmongFilteredItems(find, friendlyURL);
        },

        /**
         * Get top menu item for current URL
         * @param  {string} friendlyURL
         * @return {Object} Parent item menu with children
         */
        getCurrentParentItem: function(friendlyURL) {
            const sportsbookRootURL = this.getSportsbookRootURL();

            function find(items, path) {
                return _.find(items, function(item) {
                    return item.url === path
                        || utils.deepFind(item.children, 'children', 'friendlyURL', path);
                });
            }

            let filteredItems = this._findAmongFilteredItems(find, friendlyURL);

            if (_.isEmpty(filteredItems) && this.isSportPage(urlUtil.pathname)) {
                filteredItems = this._findAmongFilteredItems(find, sportsbookRootURL) || [];
            }

            return filteredItems;
        },

        /**
         * Get all parent items for  current URL
         * @param  {string} friendlyURL
         * @return {Array} List of menu items
         */
        getCurrentParentItems: function(friendlyURL) {
            function find(items, source) {
                var result;
                var item;

                for (var i = 0, len = items.length; i < len; i++) {
                    item = items[i];

                    if (item.friendlyURL === source) {
                        return [];
                    } else if (item.children) {
                        result = find(item.children, source);
                    }

                    if (result) {
                        result.unshift(item);
                        return result;
                    }
                }

                return null;
            }

            return this._findAmongFilteredItems(find, friendlyURL) || [];
        },

        /**
         * Get current menu item for current URL
         * @param  {string} friendlyURL
         * @return {Object} Child item menu
         */
        getCurrentItem: function(friendlyURL) {
            const sportsbookRootURL = this.getSportsbookRootURL();

            function find(items, path) {
                return utils.deepFind(items, 'children', 'friendlyURL', path);
            }

            let filteredItems = this._findAmongFilteredItems(find, friendlyURL);

            if (_.isEmpty(filteredItems) && this.isSportPage(urlUtil.pathname)) {
                filteredItems = this._findAmongFilteredItems(find, sportsbookRootURL);
            }

            return filteredItems;
        },

        /**
         * Designed for sampling menu item(s) by url with custom find method
         * @param {Function} find
         * @param {string} friendlyURL
         * @return {*}
         * @private
         */
        _findAmongFilteredItems: function(find, friendlyURL) {
           return find(this.getFilteredByRoleItems(), this.getCurrentPageURL(friendlyURL));
        },

        _handleLeaderBoardPage: function(pageUrl) {
            // in case page url has format as `/leaderboard_page/leaderboard_id`,
            // the appropriate menu item should be marked as active

            var leaderBoardPage = config.get('leaderboard.page');
            var re = new RegExp('^\\' + leaderBoardPage + '/\\d+');

            return leaderBoardPage && re.test(urlUtil.getCleanUrl(pageUrl)) ? leaderBoardPage : pageUrl;
        },

        /**
         * Check if page is sport page
         * @param  {string} path
         * @return {boolean}
         */
        isSportPage: function(path) {
            const sportsbookRootURL = this.getSportsbookRootURL();

            const sportPageRegExp = new RegExp(`(^${sportsbookRootURL}$)|(^${sportsbookRootURL}/)`);

            return sportsbookRootURL ? sportPageRegExp.test(urlUtil.getCleanUrl(path)) : false;
        },

        getSportsbookRootURL: function() {
            const lang = user.get('lang');
            const sportsbookRootURLLocaleMapping = config.get('sportsbookRootURLLocaleMapping');

            return sportsbookRootURLLocaleMapping[lang];
        }
    });

    module.exports = MenuModel;
});
